/**
 * The browser's `@/lib/sync-events`: the same exports and the same behaviour
 * as `apps/mobile/src/lib/sync-events.ts`, with one difference.
 *
 * A browser WebSocket cannot send headers, so the tenant travels as
 * `?tenant=<uuid>` and the session cookie travels on its own. The server reads
 * the query only on this upgrade and runs the same membership check it runs on
 * the header (`apps/server/src/routes/events.ts`).
 */
import { apiBaseUrl } from '@/lib/api';
import { pull } from '@/lib/sync-engine';
import type { SyncEventMessage } from '@/lib/sync-types';

const BACKOFF_MS = [1000, 2000, 5000, 10000, 20000, 30000];

let socket: WebSocket | null = null;
let activeTenant: string | null = null;
let stopped = true;
let attempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

const eventsUrl = (tenantId: string): string =>
  `${apiBaseUrl.replace(/^http/, 'ws')}/v1/events?tenant=${encodeURIComponent(tenantId)}`;

const clearReconnectTimer = (): void => {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
};

const scheduleReconnect = (): void => {
  if (stopped || activeTenant === null || reconnectTimer !== null) return;
  const delay = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)] ?? 30000;
  attempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
};

const connect = (): void => {
  const tenantId = activeTenant;
  if (stopped || tenantId === null) return;
  const ws = new WebSocket(eventsUrl(tenantId));
  socket = ws;
  ws.onopen = () => {
    if (socket !== ws) return;
    attempts = 0;
    void pull(tenantId);
  };
  ws.onmessage = (event: MessageEvent<unknown>) => {
    if (socket !== ws) return;
    try {
      const message = JSON.parse(String(event.data)) as SyncEventMessage;
      if (message.type === 'seq') void pull(tenantId);
    } catch {
      /* Not a ping shape we know. The socket carries no data, so nothing is lost. */
    }
  };
  ws.onclose = () => {
    if (socket !== ws) return;
    socket = null;
    scheduleReconnect();
  };
  ws.onerror = () => {
    ws.close();
  };
};

export const startEvents = (tenantId: string): void => {
  if (!stopped && activeTenant === tenantId) return;
  stopEvents();
  stopped = false;
  activeTenant = tenantId;
  attempts = 0;
  connect();
};

export const stopEvents = (): void => {
  stopped = true;
  activeTenant = null;
  clearReconnectTimer();
  const ws = socket;
  socket = null;
  ws?.close();
};
